import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import BackgroundAudioControls from '@/components/BackgroundAudioControls';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';
import { useBackgroundAudio } from '@/hooks/useBackgroundAudio';
import { toast } from '@/hooks/use-toast';
import { Mic, Music, Play, Pause, Loader2 } from 'lucide-react';
import { useVoices } from "@/hooks/useVoices";
import { useBackgroundMusicOptions } from "@/hooks/useBackgroundMusic";
import { useAudioPreview } from '@/hooks/useAudioPreview';

// Helper to get preview URL (uses smaller 15-second preview files)
const getBackgroundMusicPreviewUrl = (filename: string): string => {
  const baseUrl = import.meta.env.VITE_BACKEND_URL || '/api/v1';
  return `${baseUrl}/background-music/preview/${filename}`;
};

export const AudioPreferences = () => {
  const { voices } = useVoices();
  const allVoices = voices ? [...voices.male, ...voices.female] : [];
  const { options: backgroundMusicOptions } = useBackgroundMusicOptions();
  const { isPlaying, isLoading, currentPreview, playPreview, stopPreview } = useAudioPreview();

  const voiceOptions = allVoices.map(voice => ({
    value: voice.id,
    label: `${voice.name} - ${voice.description}`,
    audioKey: voice.audio_key
  }));

  const {
    preferences,
    updatePreference,
    updatingPreference,
  } = useOptimizedUserPreferences();

  const { backgroundVolume, updateBackgroundVolume } = useBackgroundAudio();

  // Get current preferences
  const currentVoice = preferences?.find(p => p.preference_type === 'voice_gender')?.preference_value || 'male_default';
  const currentBackgroundMusic = preferences?.find(p => p.preference_type === 'background_music_selection')?.preference_value || 'universal_background.mp3';
  const backgroundMusicEnabled = preferences?.find(p => p.preference_type === 'background_music')?.preference_value === 'enabled';

  const handleVoiceChange = async (value: string) => {
    try {
      await updatePreference({
        category: 'audio',
        preferenceType: 'voice_gender',
        value: value,
        confidenceBoost: 0.9
      });
      toast({
        title: "Voice preference updated",
        description: `Voice changed to ${voiceOptions.find(v => v.value === value)?.label}`,
      });
    } catch (error) {
      toast({
        title: "Error updating voice preference",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const handleBackgroundMusicSelection = async (value: string) => {
    try {
      await updatePreference({
        category: 'audio',
        preferenceType: 'background_music_selection',
        value: value,
        confidenceBoost: 0.8
      });
      toast({
        title: "Background music updated",
        description: `Music changed to ${backgroundMusicOptions.find(m => m.filename === value)?.name}`,
      });
    } catch (error) {
      toast({
        title: "Error updating background music",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const handleBackgroundMusicToggle = async (enabled: boolean) => {
    try {
      await updatePreference({
        category: 'audio',
        preferenceType: 'background_music',
        value: enabled ? 'enabled' : 'disabled',
        confidenceBoost: 0.9
      });
      toast({
        title: "Background music preference updated",
        description: `Background music ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error) {
      toast({
        title: "Error updating background music preference",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const handlePreviewToggle = async (musicFile: string) => {
    try {
      const audioUrl = getBackgroundMusicPreviewUrl(musicFile);
      await playPreview(audioUrl, musicFile);
    } catch (error) {
      console.error('Failed to preview audio:', error);
      toast({
        title: 'Preview failed',
        description: 'Could not play audio preview',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Voice Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Voice Selection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="voice-select">Meditation Voice</Label>
            <Select value={currentVoice} onValueChange={handleVoiceChange} disabled={updatingPreference}>
              <SelectTrigger>
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {voiceOptions.map((voice) => (
                  <SelectItem key={voice.value} value={voice.value}>
                    {voice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Background Music Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Music className="h-5 w-5" />
            Background Music
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="background-music-toggle">Enable Background Music</Label>
              <Switch
                id="background-music-toggle"
                checked={backgroundMusicEnabled}
                onCheckedChange={handleBackgroundMusicToggle}
                disabled={updatingPreference}
              />
            </div>

            {backgroundMusicEnabled && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label htmlFor="music-select">Select Background Music</Label>
                  <Select value={currentBackgroundMusic} onValueChange={handleBackgroundMusicSelection} disabled={updatingPreference}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select background music" />
                    </SelectTrigger>
                    <SelectContent>
                      {backgroundMusicOptions.map((music) => (
                        <SelectItem key={music.filename} value={music.filename}>
                          <div className="flex items-center justify-between w-full">
                            <div>
                              <div className="font-medium">{music.name}</div>
                              <div className="text-xs text-muted-foreground">{music.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview buttons for each music option */}
                <div className="space-y-2">
                  <Label className="text-sm">Preview Options</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                    {backgroundMusicOptions.map((music) => (
                      <div key={music.filename} className="flex items-center justify-between p-2 border rounded hover:bg-muted/50">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{music.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{music.description}</div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewToggle(music.filename)}
                          className="ml-2 flex-shrink-0"
                          disabled={currentPreview === music.filename && isLoading}
                        >
                          {currentPreview === music.filename && isLoading ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              Loading
                            </>
                          ) : currentPreview === music.filename && isPlaying ? (
                            <>
                              <Pause className="w-3 h-3 mr-1" />
                              Stop
                            </>
                          ) : (
                            <>
                              <Play className="w-3 h-3 mr-1" />
                              Preview
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <BackgroundAudioControls />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
